import { useState, useEffect, useRef, useMemo } from 'react'
import { sanitizeHtml } from '../../lib/utils/sanitizeHtml'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import {
  FileText,
  Edit3,
  Save,
  X,
  Eye,
  EyeOff,
  Code,
  Type,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'
import { usePermissionStore } from '../../stores/permissionStore'
import { friendlyApiError } from '../../lib/utils/apiError'

interface TermsPolicy {
  id: number
  policy_key: string
  title: string
  body_html: string
  is_published: boolean
  updated_by: number | null
  created_at: string
  updated_at: string
}

export default function AdminTermsPrivacy() {
  const { can } = usePermissionStore()
  const canEdit = can('terms_privacy', 'edit')

  const [policies, setPolicies] = useState<TermsPolicy[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ title: '', body_html: '' })
  const [saving, setSaving] = useState(false)
  const [successId, setSuccessId] = useState<number | null>(null)
  const [previewId, setPreviewId] = useState<number | null>(null)
  const [editorMode, setEditorMode] = useState<'visual' | 'html'>('visual')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    fetchPolicies()
  }, [])

  const fetchPolicies = async () => {
    try {
      setFetchError(null)
      const data = await adminApi.getTermsPolicies()
      setPolicies(Array.isArray(data) ? data : [])
    } catch (err) {
      setFetchError(friendlyApiError(err, 'Failed to load Terms & Privacy policies'))
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePublished = async (p: TermsPolicy) => {
    if (!canEdit) return
    try {
      const updated = await adminApi.updateTermsPolicy(p.id, { is_published: !p.is_published })
      setPolicies(policies.map((x) => (x.id === p.id ? updated : x)))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to update policy'))
    }
  }

  const handleStartEdit = (p: TermsPolicy) => {
    setEditingId(p.id)
    setEditForm({ title: p.title, body_html: p.body_html })
    setExpandedId(p.id)
    setEditorMode('visual')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditForm({ title: '', body_html: '' })
  }

  const handleSaveEdit = async (p: TermsPolicy) => {
    setSaving(true)
    try {
      const updated = await adminApi.updateTermsPolicy(p.id, editForm)
      setPolicies(policies.map((x) => (x.id === p.id ? updated : x)))
      setEditingId(null)
      setSuccessId(p.id)
      setTimeout(() => setSuccessId(null), 2500)
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to save policy'))
    } finally {
      setSaving(false)
    }
  }

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['blockquote'],
        ['clean'],
      ],
    }),
    [],
  )

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link', 'blockquote',
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div className="spinner" />
        <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>Loading policies...</span>
      </div>
    )
  }

  if (fetchError) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <FileText size={48} style={{ opacity: 0.3, marginBottom: 12, color: '#ef4444' }} />
        <h3 style={{ fontWeight: 600, fontSize: 16, color: '#ef4444' }}>Failed to Load Policies</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>{fetchError}</p>
        <button className="btn btn-primary" onClick={fetchPolicies} style={{ padding: '8px 20px', fontSize: 13 }}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            <FileText size={24} style={{ marginRight: 10, verticalAlign: 'middle', color: 'var(--accent-gold)' }} />
            Terms &amp; Privacy
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
            Manage all public content pages: Terms &amp; Conditions, Payment Policy, Privacy Policy, Support page, and Zoom App Documentation.
            {' '}The currently <strong>published</strong> content is live at the corresponding public URL.
          </p>
        </div>
      </div>

      {/* Helpful tip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '12px 14px',
          background: 'rgba(59, 130, 246, 0.06)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: 8,
          marginBottom: 20,
          fontSize: 13,
          color: '#1e40af',
        }}
      >
        <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Changes take effect immediately. Clients booking a session will see the updated text the next time they open the policy popup.
        </span>
      </div>

      {policies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
          <FileText size={40} style={{ opacity: 0.3 }} />
          <p style={{ marginTop: 12, fontSize: 14 }}>No policies yet. Run the seed script to create the defaults.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {policies.map((p) => {
            const isExpanded = expandedId === p.id
            const isEditing = editingId === p.id
            const isPreviewing = previewId === p.id

            return (
              <div
                key={p.id}
                className="card"
                style={{
                  padding: 0,
                  overflow: 'hidden',
                  border: '1px solid var(--border)',
                  background: 'white',
                }}
              >
                {/* Row header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 16,
                    cursor: isEditing ? 'default' : 'pointer',
                    borderBottom: isExpanded ? '1px solid var(--border)' : 'none',
                  }}
                  onClick={() => {
                    if (!isEditing) setExpandedId(isExpanded ? null : p.id)
                  }}
                >
                  <FileText size={18} style={{ color: 'var(--accent-gold)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-dark)' }}>
                      {p.title}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      Key: <code style={{ fontSize: 11 }}>{p.policy_key}</code> · Updated {new Date(p.updated_at).toLocaleString()}
                    </div>
                  </div>

                  {/* Success flash */}
                  {successId === p.id && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#16a34a',
                      }}
                    >
                      <CheckCircle size={14} /> Saved
                    </span>
                  )}

                  {/* Published toggle */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleTogglePublished(p) }}
                    disabled={!canEdit}
                    title={p.is_published ? 'Published — click to unpublish' : 'Unpublished — click to publish'}
                    className={`badge ${p.is_published ? 'badge-success' : 'badge-warning'}`}
                    style={{
                      cursor: canEdit ? 'pointer' : 'default',
                      border: 'none',
                      opacity: canEdit ? 1 : 0.6,
                    }}
                  >
                    {p.is_published ? 'Published' : 'Unpublished'}
                  </button>

                  {/* Expand icon */}
                  {!isEditing && (isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
                </div>

                {/* Expanded body */}
                {isExpanded && (
                  <div style={{ padding: 16 }}>
                    {!isEditing ? (
                      <>
                        {/* Read view */}
                        <div
                          className="policy-preview"
                          style={{
                            padding: 16,
                            background: 'var(--page-bg)',
                            borderRadius: 8,
                            fontSize: 14,
                            lineHeight: 1.6,
                          }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.body_html) }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleStartEdit(p)}
                              style={{ padding: '8px 16px', fontSize: 13 }}
                            >
                              <Edit3 size={14} /> Edit
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => setPreviewId(isPreviewing ? null : p.id)}
                            style={{ padding: '8px 16px', fontSize: 13 }}
                          >
                            {isPreviewing ? <><EyeOff size={14} /> Hide preview</> : <><Eye size={14} /> Show client-side preview</>}
                          </button>
                        </div>

                        {isPreviewing && (
                          <div style={{ marginTop: 16, padding: 20, background: 'white', border: '1px solid var(--border)', borderRadius: 10 }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--primary-navy)' }}>
                              {p.title}
                            </h3>
                            <div
                              className="policy-preview"
                              style={{ fontSize: 14, lineHeight: 1.6 }}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.body_html) }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Edit form */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                              Title
                            </label>
                            <input
                              className="form-input"
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                              style={{ width: '100%' }}
                            />
                          </div>

                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                                Body
                              </label>
                              <div className="tabs" style={{ display: 'flex', gap: 4 }}>
                                <button
                                  type="button"
                                  className={`tab ${editorMode === 'visual' ? 'active' : ''}`}
                                  onClick={() => setEditorMode('visual')}
                                  style={{ fontSize: 12, padding: '4px 10px' }}
                                >
                                  <Type size={12} /> Visual
                                </button>
                                <button
                                  type="button"
                                  className={`tab ${editorMode === 'html' ? 'active' : ''}`}
                                  onClick={() => setEditorMode('html')}
                                  style={{ fontSize: 12, padding: '4px 10px' }}
                                >
                                  <Code size={12} /> HTML
                                </button>
                              </div>
                            </div>

                            {editorMode === 'visual' ? (
                              <div style={{ background: 'white', borderRadius: 6 }}>
                                <ReactQuill
                                  theme="snow"
                                  value={editForm.body_html}
                                  onChange={(html) => setEditForm({ ...editForm, body_html: html })}
                                  modules={quillModules}
                                  formats={quillFormats}
                                />
                              </div>
                            ) : (
                              <textarea
                                ref={textareaRef}
                                className="form-input"
                                value={editForm.body_html}
                                onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })}
                                rows={16}
                                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                              />
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleSaveEdit(p)}
                            disabled={saving}
                            style={{ padding: '8px 20px', fontSize: 13 }}
                          >
                            <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            style={{ padding: '8px 16px', fontSize: 13 }}
                          >
                            <X size={14} /> Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <style>{`
        .policy-preview ul { padding-left: 20px; }
        .policy-preview ol { padding-left: 20px; }
        .policy-preview p { margin: 0 0 10px; }
        .policy-preview li { margin: 4px 0; }
      `}</style>
    </div>
  )
}
